import clsx from 'clsx'

import { IconSprites } from '@constants/enums/sprites'

import { Accordion } from '@components/ui-core/accordion/accordion'
import { Column } from '@components/ui-core/column/column'
import { Icon } from '@components/ui-core/icon/icon'
import { Link } from '@components/ui-core/link/link'
import { Row } from '@components/ui-core/row/row'
import { Text } from '@components/ui-core/text/text'
import { Tooltip } from '@components/ui-core/tooltip/tooltip'

import {
  PlanKinds,
  type ProductSection,
  type PlansPageData,
  type PlanKind,
  type FeatureAvailability,
  type Feature,
} from '../types'

import styles from './features-comparison.module.scss'

const productsOrder: Array<ProductSection> = ['go', 'grammarly', 'mail', 'coda', 'shared']

export const FeaturesComparison = ({
  data: { title, products },
  highlighted,
  featuresRef,
}: {
  readonly featuresRef: React.RefObject<HTMLDivElement | null>
  readonly data: PlansPageData['featuresComparisonSection']
  readonly highlighted: PlanKind
}) => {
  const accordionItems = productsOrder.flatMap(product =>
    products[product].map(({ productName, sections }) => ({
      id: productName,
      trigger: productName,
      content: <ProductAccordionItem sections={sections} highlighted={highlighted} />,
      expandedByDefault: true,
    })),
  )

  return (
    <Row ref={featuresRef} mainAxisAlignment="center" className={styles.container}>
      <Column mainAxisAlignment="start" className={styles.featuresComparison}>
        <Text as="h3" variant="headerLarge" fontWeight="medium" className={styles.title}>
          {title}
        </Text>
        <PlansNamesRow />
        <Accordion data={accordionItems} allowMultipleExpanded className={styles.accordion} />
      </Column>
    </Row>
  )
}

const ProductAccordionItem = ({
  sections,
  highlighted,
}: {
  readonly sections: Feature['sections']
  readonly highlighted: PlanKind
}) =>
  sections.map(section => (
    <table key={section.name} aria-labelledby={section.name} className={styles.featureSection}>
      <FeatureSectionNameRow featureSectionName={section.name} highlighted={highlighted} />
      <tbody>
        {section.features.map(feature => (
          <FeatureRow
            key={typeof feature.name === 'string' ? feature.name : feature.name.label}
            feature={feature}
            highlighted={highlighted}
          />
        ))}
      </tbody>
    </table>
  ))

const PlansNamesRow = () => (
  <table aria-hidden>
    <thead>
      <tr>
        <th className={clsx(styles.cell, styles.featureName, styles.planName)}>
          <Text as="span" variant="bodyLarge" fontWeight="bold">
            Feature
          </Text>
        </th>
        {PlanKinds.map((plan: PlanKind) => (
          <th key={plan} className={clsx(styles.cell, styles.planName)}>
            <Text as="span" variant="bodyLarge" fontWeight="bold">
              {plan}
            </Text>
          </th>
        ))}
      </tr>
    </thead>
  </table>
)

const FeatureSectionNameRow = ({
  featureSectionName,
  highlighted,
}: {
  readonly featureSectionName: string
  readonly highlighted: PlanKind
}) => (
  <thead>
    <tr className={styles.featureSectionRow}>
      <th className={clsx(styles.cell, styles.featureName, styles.featureSectionName)}>
        <Text as="span" variant="bodyMedium" fontWeight="bold" id={featureSectionName}>
          {featureSectionName}
        </Text>
      </th>
      {PlanKinds.map((plan: PlanKind) => (
        <th key={plan} className={clsx(styles.cell, plan === highlighted && styles.highlighted)} />
      ))}
    </tr>
  </thead>
)

const FeatureRow = ({
  feature: { name, tooltip, availability },
  highlighted,
}: {
  readonly feature: FeatureAvailability
  readonly highlighted: PlanKind
}) => (
  <tr>
    <td className={styles.featureName}>
      <Row mainAxisAlignment="start" crossAxisAlignment="center" gap="1x">
        {typeof name === 'string' ? (
          <Text as="span" variant="bodySmall">
            {name}
          </Text>
        ) : (
          <>
            {name.prefix && (
              <Text as="span" variant="bodySmall">
                {name.prefix}
              </Text>
            )}
            <Link href={name.url} target="_blank">
              <Text as="span" variant="bodySmall">
                {name.label}
              </Text>
            </Link>
          </>
        )}

        {tooltip != null && (
          <Tooltip content={tooltip} placement="right-start">
            <Icon variant={IconSprites.Info} />
          </Tooltip>
        )}
      </Row>
    </td>
    {PlanKinds.map((plan: PlanKind) => (
      <td key={plan} className={clsx(styles.cell, plan === highlighted && styles.highlighted)}>
        {availability[plan] === '✓' ? (
          <Icon variant={IconSprites.CheckMark} className={styles.checkMark} />
        ) : (
          <Text as="span" variant="bodySmall">
            {Array.isArray(availability[plan]) ? (
              <Column mainAxisAlignment="center" crossAxisAlignment="center">
                {availability[plan].map(line => (
                  <span key={line}>{line}</span>
                ))}
              </Column>
            ) : (
              availability[plan]
            )}
          </Text>
        )}
      </td>
    ))}
  </tr>
)
